// ===========================================
// COMPONENTE: GestionarCatalogoDialog
// ===========================================
// Dialog para gestionar el catálogo de impuestos (editar/eliminar)

import * as React from 'react'
import { Settings, Pencil, Trash2, Loader2, Plus, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  CATEGORIAS_IMPUESTO_CATALOGO,
  COLORES_IMPUESTO,
  type CatalogoImpuesto,
  type CategoriaImpuestoCatalogo,
  type ColorImpuesto,
} from '@/types/impuestos'

interface GestionarCatalogoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalogo: CatalogoImpuesto[]
  onActualizar: (data: ActualizarImpuestoData) => Promise<void>
  onDesactivar: (id: string) => Promise<void>
  onCrearNuevo: () => void
  isUpdating?: boolean
  isDeleting?: boolean
}

export interface ActualizarImpuestoData {
  id: string
  nombre?: string
  nombre_corto?: string
  categoria?: CategoriaImpuestoCatalogo
  color?: ColorImpuesto
}

const COLORES_DISPONIBLES: ColorImpuesto[] = [
  'gray', 'slate', 'red', 'rose', 'orange', 'amber', 'yellow',
  'green', 'emerald', 'teal', 'cyan', 'blue', 'sky', 'indigo', 'violet', 'purple',
]

export function GestionarCatalogoDialog({
  open,
  onOpenChange,
  catalogo,
  onActualizar,
  onDesactivar,
  onCrearNuevo,
  isUpdating = false,
  isDeleting = false,
}: GestionarCatalogoDialogProps) {
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [editForm, setEditForm] = React.useState<ActualizarImpuestoData | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null)
  const [busqueda, setBusqueda] = React.useState('')

  // Filtrar catálogo
  const catalogoFiltrado = React.useMemo(() => {
    if (!busqueda) return catalogo
    const query = busqueda.toLowerCase()
    return catalogo.filter(
      (imp) =>
        imp.nombre.toLowerCase().includes(query) ||
        imp.nombre_corto?.toLowerCase().includes(query) ||
        imp.codigo.toLowerCase().includes(query)
    )
  }, [catalogo, busqueda])

  // Agrupar por categoría y ordenar alfabéticamente
  const porCategoria = React.useMemo(() => {
    const grupos: Record<CategoriaImpuestoCatalogo, CatalogoImpuesto[]> = {
      iva: [],
      ganancias: [],
      iibb: [],
      patrimonial: [],
      bienes: [],
      laboral: [],
      autonomos: [],
      suss: [],
      retenciones: [],
      municipal: [],
      monotributo: [],
      otros: [],
    }

    catalogoFiltrado.forEach((imp) => {
      const cat = imp.categoria as CategoriaImpuestoCatalogo
      if (grupos[cat]) {
        grupos[cat].push(imp)
      } else {
        grupos.otros.push(imp)
      }
    })

    // Ordenar alfabéticamente dentro de cada categoría
    Object.keys(grupos).forEach((cat) => {
      grupos[cat as CategoriaImpuestoCatalogo].sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es')
      )
    })

    return grupos
  }, [catalogoFiltrado])

  // Cancelar edición
  const handleCancelEdit = React.useCallback(() => {
    setEditingId(null)
    setEditForm(null)
  }, [])

  // Cerrar edición si el impuesto editado desaparece del filtro
  React.useEffect(() => {
    if (editingId && !catalogoFiltrado.find((imp) => imp.id === editingId)) {
      handleCancelEdit()
    }
  }, [catalogoFiltrado, editingId, handleCancelEdit])

  // Iniciar edición
  const handleStartEdit = (impuesto: CatalogoImpuesto) => {
    setEditingId(impuesto.id)
    setEditForm({
      id: impuesto.id,
      nombre: impuesto.nombre,
      nombre_corto: impuesto.nombre_corto || '',
      categoria: impuesto.categoria,
      color: impuesto.color,
    })
  }

  // Guardar edición
  const handleSaveEdit = async () => {
    if (!editForm) return
    try {
      await onActualizar(editForm)
      handleCancelEdit()
    } catch (error) {
      console.error('Error actualizando impuesto:', error)
      // No limpiamos el estado para que el usuario pueda reintentar
    }
  }

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await onDesactivar(deleteConfirmId)
      setDeleteConfirmId(null)
    } catch (error) {
      console.error('Error desactivando impuesto:', error)
      // No cerramos el dialog para que el usuario pueda reintentar
    }
  }

  // Impuesto a eliminar (para mostrar nombre en confirmación)
  const impuestoAEliminar = deleteConfirmId
    ? catalogo.find((i) => i.id === deleteConfirmId)
    : null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg h-[600px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gestionar Catálogo de Impuestos
            </DialogTitle>
            <DialogDescription>
              Impuestos del sistema y personalizados de tu organización
            </DialogDescription>
          </DialogHeader>

          {/* Búsqueda - Fija, fuera del scroll */}
          <div className="px-6 pb-3 border-b">
            <Input
              placeholder="Buscar impuesto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="h-9"
            />
          </div>

          {/* Contenedor scrollable */}
          <div className="flex-1 overflow-y-auto px-6">
            {catalogoFiltrado.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-2">
                  {busqueda
                    ? `No se encontraron impuestos para "${busqueda}"`
                    : 'No hay impuestos en el catálogo'
                  }
                </p>
                {!busqueda && (
                  <Button variant="outline" onClick={onCrearNuevo}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crear primer impuesto
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-3">
                {(Object.entries(porCategoria) as [CategoriaImpuestoCatalogo, CatalogoImpuesto[]][])
                  .filter(([, impuestos]) => impuestos.length > 0)
                  .map(([categoria, impuestos]) => (
                    <div key={categoria}>
                      {/* Header de categoría */}
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-2 sticky top-0 bg-background/95 backdrop-blur-sm py-1">
                        {CATEGORIAS_IMPUESTO_CATALOGO[categoria]}
                      </p>

                      {/* Lista de impuestos en esta categoría */}
                      <div className="space-y-2">
                        {impuestos.map((impuesto) => (
                  <div
                    key={impuesto.id}
                    className={cn(
                      'border rounded-lg transition-all',
                      editingId === impuesto.id ? 'border-primary bg-muted/30' : 'hover:bg-muted/50'
                    )}
                  >
                    {editingId === impuesto.id && editForm ? (
                      // Modo edición
                      <div className="p-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre</Label>
                            <Input
                              value={editForm.nombre || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, nombre: e.target.value })
                              }
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre corto</Label>
                            <Input
                              value={editForm.nombre_corto || ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, nombre_corto: e.target.value.toUpperCase() })
                              }
                              maxLength={12}
                              className="h-8"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Categoría</Label>
                            <Select
                              value={editForm.categoria}
                              onValueChange={(v) =>
                                setEditForm({ ...editForm, categoria: v as CategoriaImpuestoCatalogo })
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(Object.entries(CATEGORIAS_IMPUESTO_CATALOGO) as [CategoriaImpuestoCatalogo, string][]).map(
                                  ([value, label]) => (
                                    <SelectItem key={value} value={value}>
                                      {label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Color</Label>
                            <div className="flex flex-wrap gap-1">
                              {COLORES_DISPONIBLES.map((c) => {
                                const colores = COLORES_IMPUESTO[c]
                                return (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => setEditForm({ ...editForm, color: c })}
                                    className={cn(
                                      'w-5 h-5 rounded border transition-all',
                                      colores.bg,
                                      editForm.color === c
                                        ? 'border-foreground scale-110'
                                        : 'border-transparent hover:scale-105'
                                    )}
                                  />
                                )
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Vista previa:</span>
                            <ImpuestoBadge
                              nombre={editForm.nombre_corto || editForm.nombre?.substring(0, 12).toUpperCase() || ''}
                              color={editForm.color || 'gray'}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEdit}
                              disabled={isUpdating}
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={isUpdating || !editForm.nombre?.trim()}
                            >
                              {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Guardar'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Modo visualización
                      <div className="p-3 flex items-center gap-2">
                        <ImpuestoBadge
                          nombre={impuesto.nombre_corto || impuesto.nombre}
                          color={impuesto.color}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium truncate">{impuesto.nombre}</p>
                            {impuesto.is_global && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="font-medium">Impuesto del sistema</p>
                                    <p className="text-xs text-muted-foreground">
                                      No se puede editar ni eliminar
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {CATEGORIAS_IMPUESTO_CATALOGO[impuesto.categoria]}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {!impuesto.is_global && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleStartEdit(impuesto)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => setDeleteConfirmId(impuesto.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer con botón crear */}
          <div className="pt-4 border-t flex justify-between">
            <Button variant="outline" onClick={onCrearNuevo}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo impuesto
            </Button>
            <Button variant="default" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar impuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción desactivará el impuesto{' '}
              <strong>{impuestoAEliminar?.nombre}</strong> del catálogo.
              Los clientes que lo tengan asignado mantendrán el registro histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  )
}
